/**
 * Sworn Translator Q&A — Wave 2 (deep-dive, self-contained answers).
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-03)
 * - "นักแปลสาบานตน" ในไทยหมายถึงผู้แปลที่สถานทูตปลายทางยอมรับหรือขึ้นบัญชีไว้
 *   ไม่ใช่ตำแหน่งที่กฎหมายไทยกำหนด แต่ละสถานทูตกำหนดเงื่อนไขของตนเอง
 * - นิติกรณ์/รับรองเอกสาร: กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ
 *   200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ประเทศไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผลกับไทย 28 ก.พ. 2027
 *   (อ้างอิง src/data/legal-facts.ts)
 * - ห้ามระบุราคาบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */
import type { SwornQA } from "./questions-500";

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — เงื่อนไขของแต่ละสถานทูตเปลี่ยนแปลงได้ โปรดยืนยันรายการเอกสารกับหน่วยงานปลายทางก่อนยื่นทุกครั้ง";

const QUOTE =
  "ค่าบริการแจ้งเป็นรายเคสทางโทรศัพท์ LINE หรืออีเมล เพราะขึ้นกับภาษา จำนวนหน้า และชั้นการรับรองที่ปลายทางกำหนด";

export const SWORN_QA_WAVE2: SwornQA[] = [
  {
    slug: "sworn-w2-ใครเรียกว่านักแปลสาบานตนในไทย",
    q: "ในประเทศไทย ใครถือว่าเป็นนักแปลสาบานตน",
    aShort:
      "ไทยไม่มีทะเบียนนักแปลสาบานตนกลางตามกฎหมาย คำว่านักแปลสาบานตนจึงหมายถึงผู้แปลที่สถานทูตปลายทางยอมรับหรือขึ้นบัญชีไว้ แต่ละสถานทูตกำหนดเงื่อนไขและรายชื่อของตนเอง",
    aLong:
      "ในประเทศฝรั่งเศส เยอรมนี อิตาลี สเปน มีตำแหน่งนักแปลสาบานตนตามกฎหมายในประเทศนั้น แต่สำหรับเอกสารที่ออกในไทย สถานทูตของประเทศเหล่านั้นในกรุงเทพจะกำหนดเองว่ายอมรับคำแปลจากใคร บางแห่งมีรายชื่อผู้แปลที่ยอมรับ บางแห่งรับคำแปลที่ผ่านการรับรองจากกรมการกงสุลก่อน ดังนั้นก่อนเริ่มงานควรอ่านหน้า legalisation หรือ traduction ของสถานทูตปลายทางเป็นหลักฐานหลัก " +
      TAIL,
    synonyms: ["นักแปลสาบานตน คือใคร", "sworn translator thailand meaning", "ไทยมีทะเบียนนักแปลสาบานตนไหม"],
    cluster: "definition",
  },
  {
    slug: "sworn-w2-ลำดับแปลสาบานตนกับนิติกรณ์",
    q: "ต้องแปลสาบานตนก่อน หรือรับรองที่กรมการกงสุลก่อน",
    aShort:
      "ลำดับที่พบบ่อยคือ เตรียมต้นฉบับราชการ แล้วจึงแปล จากนั้นนำต้นฉบับพร้อมคำแปลไปรับรองที่กองสัญชาติและนิติกรณ์ กรมการกงสุล และประทับตราสถานทูตปลายทางเป็นขั้นสุดท้ายถ้าสถานทูตกำหนด",
    aLong:
      "การสลับลำดับเป็นสาเหตุการตีกลับที่พบบ่อยที่สุด เพราะการรับรองแต่ละชั้นอ้างอิงเอกสารชั้นก่อนหน้า ถ้านำต้นฉบับไปรับรองแล้วค่อยแปลทีหลัง คำแปลจะไม่ได้อยู่ในชุดที่ถูกรับรอง ทำให้ต้องเข้าคิวใหม่ ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลคือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน ส่วนค่าธรรมเนียมสถานทูตแต่ละแห่งกำหนดเอง " +
      TAIL,
    synonyms: ["แปลก่อนหรือรับรองก่อน", "ลำดับรับรองเอกสารไปยุโรป", "order sworn translation legalisation"],
    cluster: "process",
  },
  {
    slug: "sworn-w2-apostille-ใช้แทนได้ไหม",
    q: "ขอ Apostille จากไทยแทนการรับรองสถานทูตได้ไหม",
    aShort:
      "ยังไม่ได้ ณ ส.ค. 2026 ประเทศไทยยังไม่ออก Apostille เอกสารไทยที่จะใช้ต่างประเทศจึงยังต้องผ่านนิติกรณ์ของกรมการกงสุล และประทับตราสถานทูตปลายทางตามที่แต่ละแห่งกำหนด",
    aLong:
      "อนุสัญญา Apostille จะมีผลกับประเทศไทยในวันที่ 28 กุมภาพันธ์ 2027 ก่อนถึงวันดังกล่าว หน่วยงานไทยยังไม่มีอำนาจออกใบ Apostille ถ้าปลายทางระบุคำว่า Apostille ในเช็กลิสต์ ให้แจ้งปลายทางว่าเอกสารไทยยังใช้เส้นทางนิติกรณ์และตราสถานทูตอยู่ และขอให้ยืนยันว่ารูปแบบนี้ใช้ได้ ควรเก็บอีเมลตอบกลับไว้แนบกับชุดเอกสาร " +
      TAIL,
    synonyms: ["ไทยออก apostille ได้ไหม", "apostille thailand 2027", "แปลสาบานตนกับ apostille"],
    cluster: "apostille",
  },
  {
    slug: "sworn-w2-สะกดชื่อไม่ตรงพาสปอร์ต",
    q: "ถ้าคำแปลสะกดชื่อไม่ตรงกับพาสปอร์ตจะเกิดอะไรขึ้น",
    aShort:
      "เป็นสาเหตุตีกลับอันดับต้น ๆ ชื่อบุคคลในคำแปลต้องสะกดตรงกับหน้าพาสปอร์ตทุกตัวอักษร รวมถึงชื่อกลางและลำดับชื่อสกุล ถ้าเคยเปลี่ยนชื่อต้องแนบใบเปลี่ยนชื่อที่แปลไว้ด้วย",
    aLong:
      "ผู้ตรวจเอกสารของสถานทูตเทียบชื่อแบบตัวต่อตัวกับพาสปอร์ต ไม่ได้อ่านเชิงความหมาย จึงควรส่งสำเนาหน้าพาสปอร์ตของทุกคนที่ปรากฏชื่อในเอกสารให้ผู้แปลตั้งแต่ต้น ถ้าเอกสารเก่าใช้การสะกดแบบเดิม ให้แนบเอกสารเชื่อมโยง เช่น ใบเปลี่ยนชื่อสกุลหรือหนังสือรับรองจากอำเภอ เพื่อให้ผู้ตรวจเห็นสายการเปลี่ยนชื่อครบ " +
      TAIL,
    synonyms: ["ชื่อในคำแปลไม่ตรงพาสปอร์ต", "แปลชื่อผิด สถานทูตตีกลับ", "name spelling sworn translation"],
    cluster: "acceptance",
  },
  {
    slug: "sworn-w2-คำแปลมีอายุไหม",
    q: "คำแปลที่รับรองแล้วมีอายุการใช้งานไหม",
    aShort:
      "ตัวคำแปลไม่มีวันหมดอายุในตัวเอง แต่หลายสถานทูตกำหนดว่าเอกสารต้นฉบับและตราประทับต้องออกไม่เกิน 3-6 เดือนก่อนยื่น จึงเท่ากับคำแปลมีอายุตามเงื่อนไขของปลายทาง",
    aLong:
      "เอกสารที่มักถูกกำหนดอายุคือ ใบรับรองโสด หนังสือรับรองความประพฤติ ทะเบียนบ้าน และหนังสือรับรองบริษัท เพราะสถานะเปลี่ยนได้ตลอด ส่วนสูติบัตรและใบเปลี่ยนชื่อมักไม่กำหนดอายุ แต่ตราประทับรับรองอาจถูกกำหนดอายุแทน วิธีวางแผนคือนับถอยหลังจากวันนัดยื่น แล้วเริ่มขอต้นฉบับใหม่ให้อยู่ในกรอบเวลาที่ปลายทางยอมรับ " +
      TAIL,
    synonyms: ["คำแปลอายุกี่เดือน", "เอกสารแปลหมดอายุไหม", "validity of certified translation"],
    cluster: "acceptance",
  },
  {
    slug: "sworn-w2-ต้นฉบับตัวจริงหรือสำเนา",
    q: "ต้องใช้ต้นฉบับตัวจริง หรือสำเนารับรองก็พอ",
    aShort:
      "ส่วนใหญ่ต้องใช้ต้นฉบับที่ออกโดยหน่วยงานราชการไทยตัวจริง หรือสำเนาที่หน่วยงานผู้ออกรับรอง เพราะขั้นนิติกรณ์ตรวจตราครุฑและลายมือชื่อเจ้าหน้าที่บนเอกสารจริง",
    aLong:
      "สำเนาที่ถ่ายเองแล้วเซ็นรับรองตนเองมักไม่ผ่านขั้นนิติกรณ์ ทางที่ปลอดภัยคือขอคัดสำเนาจากอำเภอหรือเขตที่ออกเอกสาร ซึ่งจะมีตราและลายมือชื่อเจ้าหน้าที่กำกับ สำหรับงานเริ่มต้น สามารถส่งไฟล์สแกนให้ทีมงานตรวจรูปแบบและเริ่มร่างคำแปลได้ก่อน แล้วค่อยส่งต้นฉบับเมื่อถึงขั้นยื่นรับรอง " +
      TAIL,
    synonyms: ["ต้องใช้ตัวจริงไหม", "สำเนารับรองใช้แปลได้ไหม", "original document required legalisation"],
    cluster: "documents",
  },
  {
    slug: "sworn-w2-อยู่ต่างจังหวัดทำอย่างไร",
    q: "อยู่ต่างจังหวัดหรือต่างประเทศ ใช้บริการแปลสาบานตนได้ไหม",
    aShort:
      "ได้ ขั้นตรวจเอกสารและร่างคำแปลทำจากไฟล์สแกนได้ทั้งหมด ส่วนต้นฉบับส่งไปรษณีย์ลงทะเบียนหรือขนส่งเอกชนมาให้ทีมงานดำเนินการขั้นรับรอง แล้วส่งชุดที่เสร็จกลับตามที่อยู่ที่แจ้ง",
    aLong:
      "แนวทางที่ลดความเสี่ยงคือ ส่งไฟล์สแกนความละเอียดสูงให้ตรวจก่อน เพื่อยืนยันว่าเอกสารครบและรูปแบบถูกต้องตามเช็กลิสต์ปลายทาง จากนั้นค่อยส่งต้นฉบับ วิธีนี้ช่วยเลี่ยงกรณีส่งเอกสารข้ามจังหวัดแล้วพบภายหลังว่าต้องคัดใหม่ที่อำเภอ ระยะเวลารวมขึ้นกับคิวของหน่วยงานราชการและสถานทูตในช่วงนั้น " +
      QUOTE +
      " " +
      TAIL,
    synonyms: ["แปลสาบานตนต่างจังหวัด", "ส่งเอกสารทางไปรษณีย์แปล", "sworn translation remote thailand"],
    cluster: "process",
  },
  {
    slug: "sworn-w2-ต้องแปลตราครุฑและลายเซ็นไหม",
    q: "ในคำแปลต้องระบุตราประทับและลายมือชื่อในเอกสารด้วยไหม",
    aShort:
      "ต้องระบุ คำแปลที่ผู้ตรวจยอมรับจะอธิบายทุกองค์ประกอบบนหน้าเอกสาร ทั้งตราครุฑ ตราประทับหน่วยงาน ลายมือชื่อ และข้อความที่พิมพ์ไว้ล่วงหน้า โดยเขียนกำกับว่าเป็นตราหรือลายมือชื่อ",
    aLong:
      "ผู้ตรวจปลายทางเทียบคำแปลกับต้นฉบับแบบหน้าต่อหน้า ถ้าคำแปลข้ามส่วนใดไป จะถูกมองว่าไม่ครบถ้วน แนวปฏิบัติที่ปลอดภัยคือใส่วงเล็บอธิบาย เช่น ระบุว่าเป็นตราประทับของหน่วยงานใด และมีลายมือชื่อพร้อมตำแหน่งของผู้ลงนามอยู่ตรงไหน รวมถึงคงเลขที่เอกสารและเลขหน้าไว้ตรงตำแหน่งเดิม " +
      TAIL,
    synonyms: ["แปลตราครุฑ", "คำแปลต้องมีคำอธิบายตราไหม", "translate seal and signature"],
    cluster: "documents",
  },
  {
    slug: "sworn-w2-แปลกลับเป็นไทย",
    q: "เอกสารต่างประเทศที่จะใช้ในไทย ต้องทำอย่างไร",
    aShort:
      "ทิศทางกลับกัน คือให้หน่วยงานหรือสถานทูตของประเทศต้นทางรับรองเอกสารก่อน แล้วแปลเป็นไทย จากนั้นนำไปรับรองที่กองสัญชาติและนิติกรณ์ กรมการกงสุล ก่อนยื่นหน่วยงานไทย",
    aLong:
      "หน่วยงานไทยจำนวนมาก เช่น อำเภอ เขต ศาล และกรมที่ดิน ต้องการคำแปลภาษาไทยที่ผ่านการรับรองของกรมการกงสุล ถ้าเอกสารออกจากประเทศที่ใช้ Apostille ให้ขอ Apostille จากประเทศต้นทางมาก่อน แล้วจึงแปลทั้งตัวเอกสารและใบ Apostille เป็นภาษาไทย เพราะผู้ตรวจไทยต้องอ่านทุกหน้าที่แนบมา " +
      TAIL,
    synonyms: ["แปลเอกสารต่างประเทศใช้ในไทย", "เอกสารต่างชาติยื่นอำเภอ", "foreign document use in thailand"],
    cluster: "process",
  },
  {
    slug: "sworn-w2-ราคาและระยะเวลา",
    q: "ค่าบริการและระยะเวลาแปลสาบานตนคิดอย่างไร",
    aShort:
      "เว็บไซต์ไม่แสดงราคาบริการ เพราะค่าบริการขึ้นกับภาษา จำนวนหน้า ความซับซ้อน และชั้นการรับรองที่ปลายทางกำหนด สอบถามได้ทางโทรศัพท์ LINE หรืออีเมล พร้อมส่งไฟล์สแกนเพื่อประเมิน",
    aLong:
      "สิ่งที่ระบุเป็นตัวเลขได้แน่นอนคือค่าธรรมเนียมราชการ ได้แก่ นิติกรณ์ของกรมการกงสุล 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน ส่วนค่าธรรมเนียมสถานทูตปลายทางเป็นอัตราของสถานทูตนั้นและอาจผูกกับอัตราแลกเปลี่ยน ระยะเวลารวมเป็นช่วงโดยประมาณ เพราะขึ้นกับคิวของหน่วยงานในแต่ละสัปดาห์ " +
      QUOTE +
      " " +
      TAIL,
    synonyms: ["ค่าแปลสาบานตน", "แปลสาบานตนกี่วัน", "sworn translation cost thailand"],
    cluster: "price",
  },
  {
    slug: "sworn-w2-เอกสารชุดวีซ่าเชงเก้น",
    q: "ชุดเอกสารยื่นวีซ่าเชงเก้นต้องแปลอะไรบ้าง",
    aShort:
      "ขึ้นกับประเภทวีซ่าและประเทศที่ยื่น ชุดที่พบบ่อยคือ ทะเบียนบ้าน สูติบัตร ทะเบียนสมรสหรือใบรับรองโสด หนังสือรับรองการทำงาน และเอกสารการเงิน โดยแต่ละสถานทูตกำหนดรายการของตนเอง",
    aLong:
      "ก่อนเริ่มแปล ให้ดาวน์โหลดเช็กลิสต์ฉบับล่าสุดจากเว็บไซต์สถานทูตหรือศูนย์รับคำร้องที่ได้รับมอบหมาย เพราะรายการเปลี่ยนตามปีและตามประเภทคำร้อง จุดที่มักพลาดคือแปลเฉพาะเอกสารหลักแต่ลืมเอกสารประกอบ เช่น ใบเปลี่ยนชื่อของผู้ค้ำหรือหนังสือยินยอมของผู้ปกครอง การส่งเช็กลิสต์มาให้ทีมงานอ่านก่อน ช่วยจัดชุดให้ครบในรอบเดียว " +
      TAIL,
    synonyms: ["เอกสารวีซ่าเชงเก้นต้องแปลอะไร", "schengen document translation list", "แปลเอกสารยื่นวีซ่ายุโรป"],
    cluster: "embassy",
  },
  {
    slug: "sworn-w2-สถานทูตแต่ละแห่งต่างกันอย่างไร",
    q: "ทำไมแต่ละสถานทูตกำหนดรูปแบบคำแปลไม่เหมือนกัน",
    aShort:
      "เพราะแต่ละประเทศใช้กฎหมายภายในของตนกำหนดว่าใครรับรองคำแปลได้ บางประเทศยอมรับคำแปลที่ผ่านนิติกรณ์ไทย บางประเทศต้องการผู้แปลในบัญชีของสถานทูต หรือผู้แปลที่สาบานตนในประเทศนั้น",
    aLong:
      "ผลในทางปฏิบัติคือ เอกสารชุดเดียวกันอาจต้องจัดรูปแบบต่างกันเมื่อยื่นคนละประเทศ เช่น บางแห่งต้องการคำแปลเย็บติดกับต้นฉบับและประทับตราคร่อม บางแห่งต้องการคำแปลแยกเล่มพร้อมหน้าปะรับรอง ทางที่ประหยัดเวลาที่สุดคือยืนยันรูปแบบกับสถานทูตปลายทางเป็นลายลักษณ์อักษรก่อนเริ่มงาน แล้วเก็บคำตอบไว้เป็นหลักฐานประกอบชุดเอกสาร " +
      TAIL,
    synonyms: ["ทำไมสถานทูตกำหนดไม่เหมือนกัน", "รูปแบบคำแปลแต่ละสถานทูต", "embassy translation requirements differ"],
    cluster: "embassy",
  },
];
